/**
 * Đọc số tiền thành chữ tiếng Việt — bản FE của
 * `tcr-erp-be-tenant/src/modules/accounting/services/amount-to-vietnamese-words.util.ts`
 * (TCR-717), dùng cho dòng "Số tiền viết bằng chữ" của tài liệu "Đơn hàng" gửi
 * khách (TCR-1962). Giữ nguyên quy tắc đọc để hai phía không ra hai câu khác nhau.
 *
 * Đây là chữ có giá trị pháp lý trên chứng từ, nên các quy tắc dưới đây là bắt
 * buộc chứ không phải cho đẹp:
 *
 * - Hàng đơn vị là 1 mà đứng sau hàng chục ≥ 2 thì đọc 'mốt' (hai mươi mốt),
 *   không phải 'hai mươi một'.
 * - Hàng đơn vị là 5 mà đứng sau hàng chục ≥ 1 thì đọc 'lăm' (mười lăm).
 * - Hàng chục là 1 đọc 'mười', không phải 'một mươi'.
 * - Hàng chục là 0 mà còn hàng đơn vị thì chèn 'lẻ' (một trăm lẻ năm).
 * - Nhóm 3 chữ số ở GIỮA mà nhỏ hơn 100 vẫn phải đọc đủ 'không trăm'
 *   (1.005.000 = 'một triệu không trăm lẻ năm nghìn'), nếu không người đọc
 *   không biết số nằm ở hàng nào.
 *
 * Chỉ nhận phần nguyên: chứng từ tiền VND không có hào/xu, số lẻ do làm tròn
 * hoặc do dữ liệu bẩn thì cắt bỏ chứ không đọc thành 'phẩy'.
 */

const DIGITS = [
  'không',
  'một',
  'hai',
  'ba',
  'bốn',
  'năm',
  'sáu',
  'bảy',
  'tám',
  'chín',
] as const;

/** Tên các nhóm 3 chữ số, từ thấp lên cao. */
const GROUP_NAMES = ['', ' nghìn', ' triệu', ' tỷ'] as const;

/**
 * Đọc một nhóm 3 chữ số.
 *
 * `full` = true buộc đọc cả 'không trăm' — dùng cho nhóm không phải nhóm cao
 * nhất, vì bỏ đi thì 'một triệu không trăm lẻ năm nghìn' thành 'một triệu năm
 * nghìn', tức sai một bậc.
 */
function readGroup(group: number, full: boolean): string {
  const hundreds = Math.floor(group / 100);
  const tens = Math.floor((group % 100) / 10);
  const units = group % 10;
  const parts: string[] = [];

  if (hundreds > 0 || full) parts.push(`${DIGITS[hundreds]} trăm`);

  if (tens > 1) {
    parts.push(`${DIGITS[tens]} mươi`);
  } else if (tens === 1) {
    parts.push('mười');
  } else if (units > 0 && (hundreds > 0 || full)) {
    // 'lẻ' chỉ có nghĩa khi phía trước đã có hàng trăm để mà 'lẻ' so với nó.
    parts.push('lẻ');
  }

  if (units > 0) {
    if (units === 1 && tens >= 2) parts.push('mốt');
    else if (units === 5 && tens >= 1) parts.push('lăm');
    else parts.push(DIGITS[units]);
  }

  return parts.join(' ');
}

/**
 * `1234000` → `'Một triệu hai trăm ba mươi bốn nghìn đồng'`.
 *
 * Số 0 trả về 'Không đồng' — chứng từ 0đ vẫn phải in ra chữ, để trống thì người
 * duyệt không phân biệt được với lỗi in.
 */
export function amountToVietnameseWords(value: number | null | undefined): string {
  const amount = Math.floor(Math.abs(Number(value) || 0));
  if (amount === 0) return 'Không đồng';

  // Tách thành các nhóm 3 chữ số, nhóm thấp nhất trước.
  const groups: number[] = [];
  let rest = amount;
  while (rest > 0) {
    groups.push(rest % 1000);
    rest = Math.floor(rest / 1000);
  }

  // Vượt quá 'tỷ' thì gộp phần dư vào nhóm tỷ: 1.000 tỷ đọc là 'một nghìn tỷ',
  // chứ không cần đặt tên đơn vị mới.
  const spoken: string[] = [];
  for (let index = groups.length - 1; index >= 0; index -= 1) {
    const group = groups[index];
    // Nhóm rỗng ở giữa vẫn phải bỏ qua tên đơn vị, nếu không sẽ đọc thừa
    // 'không nghìn'.
    if (group === 0) continue;
    const isHighest = index === groups.length - 1;
    const unit = GROUP_NAMES[Math.min(index, GROUP_NAMES.length - 1)];
    spoken.push(`${readGroup(group, !isHighest)}${unit}`);
  }

  const sentence = spoken.join(' ').replace(/\s+/g, ' ').trim();
  // Viết hoa ĐÚNG chữ đầu câu: số âm thì chữ đầu là 'Âm', phần còn lại giữ
  // nguyên chữ thường ('Âm năm trăm nghìn đồng', không phải 'Âm Năm trăm...').
  const capitalized = `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}`;
  return Number(value) < 0 ? `Âm ${sentence} đồng` : `${capitalized} đồng`;
}
